/**
 * RaceTrace Live, track map: where the PIT tag and the chips of the cars in the pits go, kept off
 * the track.
 *
 * The tag sits beside the track at the pit-in point (PIT_TAG_PX out), on whichever side and at
 * whichever distance first clears the track. The chips run in one line from it, the way (right,
 * left, down, up) that fits the most without touching the track or the map's edge, at most `max`,
 * the rest "+n". Where none fits beside the tag (a narrow gap between two stretches of track), the
 * line goes to the nearest open space that fits the most, joined to the tag by a link. Worked out
 * only when the cars in the pits, the map's size or its angle change (the caller keeps it).
 */

export interface Box {
  x: number;
  y: number;
  w: number;
}

export interface PitChipsLayout {
  /** The PIT tag (top-left, width) and where its leader line from the track ends. */
  tag: Box;
  leader: [number, number, number, number];
  /** The chips, in the order given; "+n" after them when not all fit. */
  boxes: Box[];
  more: Box | null;
  /** From the tag to the line of chips, when the line is not beside it. */
  link: [number, number, number, number] | null;
}

/** Chip height and the gap between chips (px). */
export const CHIP_H = 14;
const GAP = 3;
/** The tag's distance out from the track's centreline (px), the first that clears it; else further, any way round. */
const TAG_OFFS = [22, 30, 40, 55];
const TAG_FAR = [70, 90, 110];
/** Searched for open space when the line does not fit beside the tag: distances (px) from it, every 45°. */
const SEARCH_R = [20, 32, 46, 62, 80, 100, 130];

/**
 * The PIT tag and chips. `outline`: the track as points every few pixels; `clearPx`: how far a box
 * keeps from them; `pitIn`: the pit-in point on the track and the unit normal out from it;
 * `widths`: each chip's width; `widthOf`: a text's chip width (for "+n" and the tag).
 */
export function layoutPitChips({
  outline,
  W,
  H,
  pitIn,
  widths,
  widthOf,
  max,
  clearPx,
  alongAcross,
  blocked = [],
}: {
  outline: [number, number][];
  W: number;
  H: number;
  pitIn: { x: number; y: number; ux: number; uy: number };
  widths: number[];
  widthOf: (text: string) => number;
  max: number;
  clearPx: number;
  /** The track runs across the map at the pit-in point (try right and left first, else down and up). */
  alongAcross: boolean;
  /** Panels floating over the map (its own px): the boxes keep off them too. */
  blocked?: { x: number; y: number; w: number; h: number }[];
}): PitChipsLayout {
  const clear = (b: Box) =>
    b.x >= 4 &&
    b.y >= 4 &&
    b.x + b.w <= W - 4 &&
    b.y + CHIP_H <= H - 4 &&
    blocked.every((r) => b.x + b.w + GAP <= r.x || r.x + r.w + GAP <= b.x || b.y + CHIP_H + GAP <= r.y || r.y + r.h + GAP <= b.y) &&
    outline.every(([qx, qy]) => Math.hypot(Math.max(b.x - qx, 0, qx - b.x - b.w), Math.max(b.y - qy, 0, qy - b.y - CHIP_H)) > clearPx);

  // The tag: out from the track either side, the nearest that clears it; else further, any way
  // round (every 45°); else the usual place.
  const tagW = widthOf("PIT 99");
  const tagAt = (ux: number, uy: number, off: number): Box => ({ x: pitIn.x + ux * off - tagW / 2, y: pitIn.y + uy * off - CHIP_H / 2, w: tagW });
  const dirs: [number, number][] = [
    [pitIn.ux, pitIn.uy],
    [-pitIn.ux, -pitIn.uy],
  ];
  for (let k = 0; k < 8; k++) dirs.push([Math.cos((k * Math.PI) / 4), Math.sin((k * Math.PI) / 4)]);
  let tag: Box | null = null;
  let tdir: [number, number] = [pitIn.ux, pitIn.uy];
  search: for (const [offs, ds] of [
    [TAG_OFFS, dirs.slice(0, 2)],
    [[...TAG_OFFS, ...TAG_FAR], dirs.slice(2)],
    [TAG_FAR, dirs.slice(0, 2)],
  ] as [number[], [number, number][]][])
    for (const off of offs)
      for (const d of ds) {
        const b = tagAt(d[0], d[1], off);
        if (clear(b)) {
          tag = b;
          tdir = d;
          break search;
        }
      }
  tag ??= tagAt(pitIn.ux, pitIn.uy, TAG_OFFS[0]);
  const tcx = tag.x + tag.w / 2;
  const tcy = tag.y + CHIP_H / 2;
  // The leader: from just off the track at the pit-in point to the tag's nearest edge.
  const ex = Math.min(Math.max(pitIn.x + tdir[0] * 9, tag.x), tag.x + tag.w);
  const ey = Math.min(Math.max(pitIn.y + tdir[1] * 9, tag.y), tag.y + CHIP_H);
  const leader: [number, number, number, number] = [pitIn.x + tdir[0] * 9, pitIn.y + tdir[1] * 9, ex, ey];

  // The chips keep off the tag too.
  const t0 = tag;
  const clearOfTag = (b: Box) => clear(b) && (b.x + b.w + GAP <= t0.x || t0.x + t0.w + GAP <= b.x || b.y + CHIP_H + GAP <= t0.y || t0.y + CHIP_H + GAP <= b.y);
  const n = widths.length;
  const want = Math.min(n, max);
  const next = (prev: Box, w: number, way: number): Box =>
    way === 0
      ? { x: prev.x + prev.w + GAP, y: prev.y, w }
      : way === 1
        ? { x: prev.x - GAP - w, y: prev.y, w }
        : way === 2
          ? { x: prev.x, y: prev.y + CHIP_H + GAP, w }
          : { x: prev.x, y: prev.y - CHIP_H - GAP, w };
  // From `start` one way: as many chips as fit (up to `want`), and "+n" after them when not all are shown.
  const line = (start: Box, way: number, first: boolean): { boxes: Box[]; more: Box | null } => {
    const fit: Box[] = [];
    let prev = start;
    for (let i = 0; i < want; i++) {
      // The first chip of a line away from the tag starts at the anchor itself.
      const b = first && i === 0 ? { x: way === 1 ? start.x - widths[0] : start.x, y: way === 3 ? start.y - CHIP_H : start.y, w: widths[0] } : next(prev, widths[i], way);
      if (!clearOfTag(b)) break;
      fit.push(b);
      prev = b;
    }
    if (fit.length === n) return { boxes: fit, more: null };
    for (let k = fit.length; k >= 0; k--) {
      const after = k ? fit[k - 1] : start;
      const w = widthOf(`+${n - k}`);
      const m = !k && first ? { x: way === 1 ? start.x - w : start.x, y: way === 3 ? start.y - CHIP_H : start.y, w } : next(after, w, way);
      if (clearOfTag(m)) return { boxes: fit.slice(0, k), more: m };
    }
    return { boxes: [], more: null };
  };
  const score = (l: { boxes: Box[]; more: Box | null }) => l.boxes.length + (l.more ? 0.5 : 0);
  const full = want + (n > max ? 0.5 : 0);
  const ways = alongAcross ? [0, 1, 2, 3] : [2, 3, 0, 1];
  if (n === 0) return { tag, leader, boxes: [], more: null, link: null };

  // Beside the tag.
  let best = { boxes: [] as Box[], more: null as Box | null };
  for (const way of ways) {
    const l = line(tag, way, false);
    if (score(l) > score(best)) best = l;
    if (score(best) === full) return { tag, leader, ...best, link: null };
  }
  // Else the nearest open space that fits more, joined to the tag.
  type Line = { boxes: Box[]; more: Box | null };
  const found: { away: Line | null } = { away: null };
  for (const r of SEARCH_R) {
    for (let k = 0; k < 8; k++) {
      const a = (k * Math.PI) / 4;
      const anchor = { x: tcx + Math.cos(a) * (r + tag.w / 2), y: tcy + Math.sin(a) * (r + CHIP_H / 2) - CHIP_H / 2, w: 0 };
      for (const way of ways) {
        const l = line(anchor, way, true);
        if (score(l) > score(found.away ?? best)) found.away = l;
      }
    }
    if (found.away && score(found.away) === full) break;
  }
  const away = found.away;
  if (!away) return { tag, leader, ...best, link: null };
  const firstBox = away.boxes[0] ?? away.more!;
  return { tag, leader, boxes: away.boxes, more: away.more, link: [tcx, tcy, firstBox.x + firstBox.w / 2, firstBox.y + CHIP_H / 2] };
}

/** The track as points every `step` px (for keeping boxes off it). */
export function outlinePoints(pts: [number, number][], step = 4): [number, number][] {
  const out: [number, number][] = [];
  for (let i = 0; i < pts.length; i++) {
    const [ax, ay] = pts[i];
    const [bx, by] = pts[(i + 1) % pts.length];
    const n = Math.max(1, Math.ceil(Math.hypot(bx - ax, by - ay) / step));
    for (let j = 0; j < n; j++) out.push([ax + ((bx - ax) * j) / n, ay + ((by - ay) * j) / n]);
  }
  return out;
}
